import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const translations = {
  en: {
    nav: {
      stay: 'Stay',
      lodgeRoom: 'Our Rooms',
      villa: 'Our Villas',
      dining: 'Dining',
      explorations: 'Itineraries and experiences',
      community: 'Community',
      gallery: 'Gallery',
      gettingHere: 'Getting here',
      contact: 'Contact',
      book: 'Reserve'
    },
    hero: {
      title: 'RESPIRO',
      subtitle: 'SÃO VICENTE',
      scroll: 'Scroll to explore'
    },
    welcome: {
      tagline: "",
      title: "",
      description: "Here's to the next explorers, those who dare to start something new with nothing else than who they are. Who open new paths just to pave the way for others.",
      secondary: "Welcome to a place where unbound exploration meets incredible comfort, Where local culture meets warm hospitality, Where body and mind embark on a conscious journey.",
      explorerTitle: "A destination for explorers",
      explorerDescription: "Sao Vicente and Santo Antao islands can feel like planet hopping. From lush valley, to arid ochre mountains, to black and beige sand beaches, every road brings a different surprise, and a sense of immensity.",
    },
    stay: {
      title: 'Stay',
      subtitle: 'In the midst of a mystifying moonscape, our intimate bay is a glorious refuge, an architectural base camp to explore the wonders of Sao Vicente and Santo Antao islands.',
      lodges: {
        title: 'Lodges',
        description: 'Our terrace seaview lodge claims mesmerising views over the ocean.',
        details: 'Sleep 2 / King or Twin beds',
        features: ['25 sqm room', 'Air conditioning', '10 sqm private terrace', 'Sea view'],
        cta: 'OPENING SPRING 2027'
      },
      villas: {
        title: 'Villas',
        description: 'Our villa provides a sanctuary of calm, harmoniously designed with privacy and nature in mind.',
        details: 'Sleep 6 to 8 / King or Twin beds',
        features: ['From 300 to 600 sqm', '3 to 5 bedrooms', 'Indoor and outdoor kitchen', 'Private pool', 'Private terrace'],
        cta: 'Check availability'
      }
    },
    dining: {
      title: 'Dining & Event',
      subtitle: '',
      description: 'Slow down, sink your feet in the sand and let the rhythm flow. Good food, great music and a poolside vibe made for long, lazy days.',
      beachbar: {
        title: 'Beach Bar & Pool',
        description: 'Slow down, sink your feet in the sand and let the rhythm flow. Good food, great music and a poolside vibe made for long, lazy days.'
      },
      restaurant: {
        title: 'Events',
        description: "A Celebration of Cape Verde's bounty, just soulful gatherings with great people, good energy and the ocean as your backdrop."
      },
      menu: 'See Menu',
      events: 'Event Requests'
    },
    explorations: {
      title: 'Itineraries and experiences',
      subtitle: "We curate itineraries and experiences tailored to your desire, always with the utmost attention to details, to build travel moments that feel unexpected, pure and raw.",
      description: 'Our explorations are built with thoughtful local guides, guardians of magical moments and natural havens. In our hands you will be someone else for a day.',
      ocean: {
        title: 'Ocean',
        description: 'For those who like to stay salty. We all wish to feel this pioneer sensation of finding unspoiled locations.',
        activities: ['Surf', 'Foil', 'Kitesurf', 'Diving', 'Fishing']
      },
      earth: {
        title: 'Earth',
        description: 'From canyons to volcanoes to ocean cliffs and authentic villages, some of the places we will show you are protected by UNESCO.',
        activities: ['Hike', 'Discovery of Santo Antao', 'Mountain Bike', 'E-Moto']
      },
      wellness: {
        title: 'Wellness & Fitness',
        description: 'Explore the wellness of body and mind recovery connected to pure elements.',
        activities: ['Massage', 'Training', 'Yoga']
      },
      morabeza: {
        title: 'Morabeza',
        description: 'The Cape Verdean culture is rich, alive and vibrates through its people, food, colors, music, and craftsmanship.',
        activities: ['Discovery of Mindelo', 'Cuisine & agriculture', 'Pottery', 'Music']
      },
      backToMain: 'Back to main page'
    },
    community: {
      title: 'Our Community',
      subtitle: 'As we share with our guest this unspoiled paradise, we give back to the ones that have opened their doors to us.',
      description: 'Our aim is to have a long lasting positive impact through environmental, social and communal art projects. A percentage of each guests\' stay contributes to empowering local communities.',
      localStories: {
        title: 'Local Stories',
        description: 'We discover the warm heart and spirits of the cultural capital of the archipelago, by meeting and sharing with its protectors and main actors in the marvelous Mindelo.'
      },
      farm: {
        title: 'Our Farm',
        description: 'We believe in regenerative agriculture. With the help of local farmers we created a farm project in Santo Antao to plant and grow naturally reproducible vegetable seeds.'
      },
      ocean: {
        title: 'Ocean Initiatives',
        description: 'In partnership with local associations, we actively participate in the preservation of the fauna and flora of Sao Vicente and Santo Antao.'
      },
      artcraft: {
        title: 'Artcraft',
        description: 'We aim to create an authentic connection between our guests and the local culture to support local craftsmanship. Our shop\'s selection showcases all the local treasures.'
      },
      readMore: 'Read more'
    },
    gallery: {
      title: 'Gallery',
      filters: {
        all: 'All',
        lodges: 'Lodges',
        villas: 'Villas',
        explorations: 'Explorations',
        events: 'Events',
        dining: 'Dining',
        beachbar: 'Beach Bar & Pool'
      }
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'A journey from a secluded bay to the ends of the Earth',
      description: '',
      form: {
        name: 'Your Name',
        email: 'Your Email',
        subject: 'Subject',
        message: 'Your Message',
        send: 'Send Message'
      },
      address: 'Baia João Evora, Mindelo, São Vicente. Cape Verde',
      email: 'info@hotelrespiro.com'
    },
    footer: {
      about: 'About us',
      services: 'Services',
      careers: 'Careers',
      press: 'Media & Press',
      rights: '© 2024 Hotel Respiro. All rights reserved.'
    }
  },
  fr: {
    nav: {
      stay: 'Séjour',
      lodgeRoom: 'Nos Chambres',
      villa: 'Nos Villas',
      dining: 'Restaurant',
      explorations: 'Itinéraires et expériences',
      community: 'Communauté',
      gallery: 'Galerie',
      gettingHere: 'Venir à Respiro',
      contact: 'Contact',
      book: 'Réserver'
    },
    hero: {
      title: 'RESPIRO',
      subtitle: 'SÃO VICENTE',
      scroll: 'Défiler pour explorer'
    },
    welcome: {
      tagline: '',
      title: '',
      description: 'À ceux qui osent commencer quelque chose de nouveau avec rien d\'autre que ce qu\'ils sont. Ceux qui ouvrent de nouveaux chemins pour tracer la voie aux autres.',
      secondary: 'Bienvenue dans un lieu où l\'exploration sans limites rencontre un confort incroyable, Où la culture locale rencontre une hospitalité chaleureuse, Où le corps et l\'esprit s\'embarquent pour un voyage conscient.',
      explorerTitle: 'Une destination pour explorateurs',
      explorerDescription: 'Les îles de Sao Vicente et Santo Antao peuvent donner l\'impression de voyager entre planètes. Des vallées luxuriantes aux montagnes ocre arides, des plages de sable noir et beige, chaque route apporte une surprise différente et un sentiment d\'immensité.',
    },
    stay: {
      title: 'Hébergement',
      subtitle: 'Au cœur d\'un paysage lunaire mystérieux, notre baie intime est un refuge glorieux, un camp de base architectural pour explorer les merveilles des îles de Sao Vicente et Santo Antao.',
      lodges: {
        title: 'Lodges',
        description: 'Notre lodge avec terrasse vue mer offre des vues envoûtantes sur l\'océan.',
        details: '2 personnes / Lit King ou Twin',
        features: ['Chambre de 25 m²', 'Climatisation', 'Terrasse privée de 10 m²', 'Vue mer'],
        cta: 'Voir tarifs & détails'
      },
      villas: {
        title: 'Villas',
        description: 'Notre villa offre un sanctuaire de calme, harmonieusement conçue avec l\'intimité et la nature à l\'esprit.',
        details: '6 à 8 personnes / Lit King ou Twin',
        features: ['De 300 à 600 m²', '3 à 5 chambres', 'Cuisine intérieure et extérieure', 'Piscine privée', 'Terrasse privée'],
        cta: 'Voir tarifs & détails'
      }
    },
    dining: {
      title: 'Dining & Event',
      subtitle: '',
      description: 'Ralentissez, enfoncez vos pieds dans le sable et laissez le rythme couler. Bonne nourriture, bonne musique et une ambiance au bord de la piscine faite pour de longues journées paresseuses.',
      beachbar: {
        title: 'Beach Bar & Piscine',
        description: 'Ralentissez, enfoncez vos pieds dans le sable et laissez le rythme couler. Bonne nourriture, bonne musique et une ambiance au bord de la piscine faite pour de longues journées paresseuses.',
        hours: 'De 8h à 23h'
      },
      restaurant: {
        title: 'Événements',
        description: 'Une célébration des richesses du Cap-Vert, des rassemblements chaleureux avec des personnes formidables, une bonne énergie et l\'océan en toile de fond.',
        hours: 'Petit-déjeuner: 8h-11h | Déjeuner: 12h-15h | Dîner: 19h-22h'
      },
      menu: 'Voir le Menu',
      events: 'Demande d\'événement'
    },
    explorations: {
      title: 'Itinéraires et expériences',
      subtitle: 'Nous créons des itinéraires et des expériences adaptés à vos désirs, toujours avec la plus grande attention aux détails, pour construire des moments de voyage inattendus, purs et authentiques.',
      description: 'Nos explorations sont construites avec des guides locaux attentionnés, gardiens de moments magiques et de havres naturels. Entre nos mains, vous serez quelqu\'un d\'autre pour un jour.',
      ocean: {
        title: 'Océan',
        description: 'Pour ceux qui aiment rester salés. Nous souhaitons tous ressentir cette sensation de pionnier en découvrant des endroits préservés.',
        activities: ['Surf', 'Foil', 'Kitesurf', 'Plongée', 'Pêche']
      },
      earth: {
        title: 'Terre',
        description: 'Des canyons aux volcans, des falaises océaniques aux villages authentiques, certains endroits que nous vous montrerons sont protégés par l\'UNESCO.',
        activities: ['Randonnée', 'Découverte de Santo Antao', 'VTT', 'E-Moto']
      },
      wellness: {
        title: 'Wellness & Fitness',
        description: 'Explorez le bien-être du corps et de l\'esprit connecté aux éléments purs.',
        activities: ['Massage', 'Entraînement', 'Yoga']
      },
      morabeza: {
        title: 'Morabeza',
        description: 'La culture cap-verdienne est riche, vivante et vibre à travers son peuple, sa nourriture, ses couleurs, sa musique et son artisanat.',
        activities: ['Découverte de Mindelo', 'Cuisine & agriculture', 'Poterie', 'Musique']
      },
      backToMain: 'Retour à la page principale'
    },
    community: {
      title: 'Notre Communauté',
      subtitle: 'Alors que nous partageons ce paradis préservé avec nos invités, nous redonnons à ceux qui nous ont ouvert leurs portes.',
      description: 'Notre objectif est d\'avoir un impact positif durable à travers des projets environnementaux, sociaux et artistiques communautaires. Un pourcentage du séjour de chaque invité contribue à l\'autonomisation des communautés locales.',
      localStories: {
        title: 'Histoires Locales',
        description: 'Nous découvrons le cœur chaleureux et les esprits de la capitale culturelle de l\'archipel, en rencontrant et partageant avec ses protecteurs et acteurs principaux dans la merveilleuse Mindelo.'
      },
      farm: {
        title: 'Notre Ferme',
        description: 'Notre ferme cultive plus que de la nourriture, elle cultive des connexions. Avec les mains des agriculteurs locaux, nous cultivons des ingrédients naturels et un respect partagé pour la terre.'
      },
      ocean: {
        title: 'Initiatives Océan',
        description: 'Notre engagement va au-delà de l\'hospitalité. Nous protégeons les tortues marines avec des associations locales, travaillant activement à garder nos plages et océans sains pour les prochaines générations.'
      },
      artcraft: {
        title: 'Artisanat',
        description: 'Créer une connexion authentique entre nos invités et la culture locale commence dans chaque chambre avec des produits fabriqués par des artisans locaux.'
      },
      readMore: 'En savoir plus'
    },
    gallery: {
      title: 'Galerie',
      filters: {
        all: 'Tout',
        lodges: 'Lodges',
        villas: 'Villas',
        explorations: 'Explorations',
        events: 'Événements',
        dining: 'Restaurant',
        beachbar: 'Beach Bar & Piscine'
      }
    },
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Un voyage d\'une baie isolée jusqu\'aux confins de la Terre',
      description: '',
      form: {
        name: 'Votre Nom',
        email: 'Votre Email',
        subject: 'Sujet',
        message: 'Votre Message',
        send: 'Envoyer le Message'
      },
      address: 'Baia João Evora, Mindelo, São Vicente. Cap-Vert',
      email: 'info@hotelrespiro.com'
    },
    footer: {
      about: 'À propos',
      services: 'Services',
      careers: 'Carrières',
      press: 'Médias & Presse',
      rights: '© 2024 Hotel Respiro. Tous droits réservés.'
    }
  },
  pt: {
    nav: {
      stay: 'Estadia',
      lodgeRoom: 'Os Nossos Quartos',
      villa: 'As Nossas Villas',
      dining: 'Restaurante',
      explorations: 'Itinerários e experiências',
      community: 'Comunidade',
      gallery: 'Galeria',
      gettingHere: 'Como chegar',
      contact: 'Contacto',
      book: 'Reservar'
    },
    hero: {
      title: 'RESPIRO',
      subtitle: 'SÃO VICENTE',
      scroll: 'Deslize para explorar'
    },
    welcome: {
      tagline: '',
      title: '',
      description: 'Aos próximos exploradores, aqueles que ousam começar algo novo sem nada além de quem são. Que abrem novos caminhos apenas para pavimentar o caminho para outros.',
      secondary: 'Bem-vindo a um lugar onde a exploração sem limites encontra um conforto incrível, Onde a cultura local encontra uma hospitalidade calorosa, Onde corpo e mente embarcam numa viagem consciente.',
      explorerTitle: 'Um destino para exploradores',
      explorerDescription: 'As ilhas de Sao Vicente e Santo Antao podem parecer uma viagem entre planetas. De vales exuberantes a montanhas ocre áridas, de praias de areia preta e bege, cada estrada traz uma surpresa diferente e uma sensação de imensidão.'
    },
    stay: {
      title: 'Estadia',
      subtitle: 'No meio de uma paisagem lunar misteriosa, a nossa baía íntima é um refúgio glorioso, um acampamento base arquitetónico para explorar as maravilhas das ilhas de Sao Vicente e Santo Antao.',
      lodges: {
        title: 'Lodges',
        description: 'O nosso lodge com terraço vista mar oferece vistas hipnotizantes sobre o oceano.',
        details: '2 pessoas / Cama King ou Twin',
        features: ['Quarto de 25 m²', 'Ar condicionado', 'Terraço privado de 10 m²', 'Vista mar'],
        cta: 'Ver tarifas & detalhes'
      },
      villas: {
        title: 'Villas',
        description: 'A nossa villa oferece um santuário de calma, harmoniosamente concebida com a privacidade e a natureza em mente.',
        details: '6 a 8 pessoas / Cama King ou Twin',
        features: ['De 300 a 600 m²', '3 a 5 quartos', 'Cozinha interior e exterior', 'Piscina privada', 'Terraço privado'],
        cta: 'Ver tarifas & detalhes'
      }
    },
    dining: {
      title: 'Dining & Event',
      subtitle: '',
      description: 'Abrande, afunde os pés na areia e deixe o ritmo fluir. Boa comida, boa música e uma vibração à beira da piscina feita para dias longos e preguiçosos.',
      beachbar: {
        title: 'Beach Bar & Piscina',
        description: 'Abrande, afunde os pés na areia e deixe o ritmo fluir. Boa comida, boa música e uma vibração à beira da piscina feita para dias longos e preguiçosos.',
        hours: 'Das 8h às 23h'
      },
      restaurant: {
        title: 'Eventos',
        description: 'Uma celebração da riqueza de Cabo Verde, encontros calorosos com pessoas fantásticas, boa energia e o oceano como pano de fundo.',
        hours: 'Pequeno-almoço: 8h-11h | Almoço: 12h-15h | Jantar: 19h-22h'
      },
      menu: 'Ver Menu',
      events: 'Pedido de evento'
    },
    explorations: {
      title: 'Itinerários e experiências',
      subtitle: 'Criamos itinerários e experiências adaptados aos seus desejos, sempre com a máxima atenção aos detalhes, para construir momentos de viagem inesperados, puros e autênticos.',
      description: 'As nossas explorações são construídas com guias locais atenciosos, guardiões de momentos mágicos e refúgios naturais. Nas nossas mãos será outra pessoa por um dia.',
      ocean: {
        title: 'Oceano',
        description: 'Para quem gosta de ficar salgado. Todos desejamos sentir essa sensação pioneira de encontrar locais intocados.',
        activities: ['Surf', 'Foil', 'Kitesurf', 'Mergulho', 'Pesca']
      },
      earth: {
        title: 'Terra',
        description: 'De canyons a vulcões, de falésias oceânicas a aldeias autênticas, alguns dos lugares que mostraremos são protegidos pela UNESCO.',
        activities: ['Caminhada', 'Descoberta de Santo Antao', 'BTT', 'E-Moto']
      },
      wellness: {
        title: 'Wellness & Fitness',
        description: 'Explore o bem-estar do corpo e da mente conectado a elementos puros.',
        activities: ['Massagem', 'Treino', 'Yoga']
      },
      morabeza: {
        title: 'Morabeza',
        description: 'A cultura cabo-verdiana é rica, viva e vibra através do seu povo, comida, cores, música e artesanato.',
        activities: ['Descoberta de Mindelo', 'Cozinha & agricultura', 'Cerâmica', 'Música']
      },
      backToMain: 'Voltar à página principal'
    },
    community: {
      title: 'Nossa Comunidade',
      subtitle: 'Ao partilhar este paraíso intocado com os nossos hóspedes, retribuímos àqueles que nos abriram as suas portas.',
      description: 'O nosso objetivo é ter um impacto positivo duradouro através de projetos ambientais, sociais e artísticos comunitários. Uma percentagem da estadia de cada hóspede contribui para capacitar as comunidades locais.',
      localStories: {
        title: 'Histórias Locais',
        description: 'Descobrimos o coração caloroso e os espíritos da capital cultural do arquipélago, conhecendo e partilhando com os seus protetores e principais atores na maravilhosa Mindelo.'
      },
      farm: {
        title: 'Nossa Fazenda',
        description: 'A nossa quinta cultiva mais do que comida, cultiva conexões. Com as mãos dos agricultores locais, cultivamos ingredientes naturais e um respeito partilhado pela terra.'
      },
      ocean: {
        title: 'Iniciativas Oceano',
        description: 'O nosso compromisso vai além da hospitalidade. Protegemos tartarugas marinhas com associações locais, trabalhando ativamente para manter as nossas praias e oceanos saudáveis para as próximas gerações.'
      },
      artcraft: {
        title: 'Artesanato',
        description: 'Criar uma conexão autêntica entre os nossos hóspedes e a cultura local começa em cada quarto com produtos criados por artesãos locais.'
      },
      readMore: 'Ler mais'
    },
    gallery: {
      title: 'Galeria',
      filters: {
        all: 'Tudo',
        lodges: 'Lodges',
        villas: 'Villas',
        explorations: 'Explorações',
        events: 'Eventos',
        dining: 'Restaurante',
        beachbar: 'Beach Bar & Piscina'
      }
    },
    contact: {
      title: 'Contacte-nos',
      subtitle: 'Uma viagem de uma baía isolada até aos confins da Terra',
      description: '',
      form: {
        name: 'Seu Nome',
        email: 'Seu Email',
        subject: 'Assunto',
        message: 'Sua Mensagem',
        send: 'Enviar Mensagem'
      },
      address: 'Baia João Evora, Mindelo, São Vicente. Cabo Verde',
      email: 'info@hotelrespiro.com'
    },
    footer: {
      about: 'Sobre nós',
      services: 'Serviços',
      careers: 'Carreiras',
      press: 'Mídia & Imprensa',
      rights: '© 2024 Hotel Respiro. Todos os direitos reservados.'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('respiro-lang') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('respiro-lang', language);
  }, [language]);

  const value = useMemo(() => {
    const t = (key) => {
      const keys = key.split('.');
      let result = translations[language];
      for (const k of keys) {
        result = result?.[k];
      }
      return result || key;
    };

    const toggleLanguage = () => {
      setLanguage(prev => {
        if (prev === 'en') return 'fr';
        if (prev === 'fr') return 'pt';
        return 'en';
      });
    };

    const cycleLanguage = (lang) => {
      setLanguage(lang);
    };

    return { language, setLanguage, toggleLanguage, cycleLanguage, t };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
